import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { api, urlImagem } from '../lib/api';
import { useAuth } from '../lib/auth';
import SeletorFotos from '../components/SeletorFotos';
import { carregarAtivosComConsumo } from './consumoHelpers';

interface Ativo {
  id: number;
  nome: string;
  unidade_id: number;
  piso_id: number;
  local_id: number;
  patrimonio?: string | null;
  marca?: string | null;
  foto_url?: string | null;
}

interface Campo {
  id: number;
  codigo: string;
  rotulo: string;
  unidade_medida?: string | null;
  ordem: number;
  obrigatorio: boolean;
  principal: boolean;
  status: string;
}

interface Medidor {
  id: number;
  ativo_id: number;
  ativo_nome?: string | null;
  tipo_consumo: 'agua' | 'gas' | 'energia_eletrica';
  nome: string;
  codigo?: string | null;
  unidade_medida: string;
  status: string;
  exigir_imagem_leitura?: boolean;
  intervalo_leitura_dias?: number | null;
  limite_alerta_percentual?: number | null;
  campos: Campo[];
  ultima_leitura?: {
    id: number;
    data_leitura: string;
    status: string;
    valores: Record<string, string>;
  } | null;
}

interface Midia {
  id: number;
  arquivo_url: string;
  tipo: 'foto' | 'video';
}

interface Leitura {
  id: number;
  medidor_id: number;
  ativo_id: number;
  data_leitura: string;
  observacao?: string | null;
  usuario_nome?: string | null;
  status: string;
  valores: Record<string, string>;
  consumos: Record<string, string | null>;
  baseline: Record<string, string>;
  midias: Midia[];
}

interface Reinicializacao {
  id: number;
  data_Reinicializacao: string;
  motivo: string;
  responsavel_nome?: string | null;
  observacao: string;
  leituras_anteriores: Record<string, string>;
  novas_leituras: Record<string, string>;
}

interface CampoForm {
  id?: number;
  codigo: string;
  rotulo: string;
  unidade_medida: string;
  obrigatorio: boolean;
  principal: boolean;
}

interface FormMedidorState {
  tipo_consumo: string;
  nome: string;
  codigo: string;
  unidade_medida: string;
  exigir_imagem_leitura: boolean;
  intervalo_leitura_dias: string;
  limite_alerta_percentual: string;
  campos: CampoForm[];
}

const hoje = () => new Date().toISOString().slice(0, 10);

function numero(v?: string | number | null): string {
  if (v === null || v === undefined || v === '') return '-';
  const n = typeof v === 'number' ? v : Number(v);
  if (Number.isNaN(n)) return String(v);
  return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 4 }).format(n);
}

function valorLeituraParaInput(v?: string | number | null): string {
  if (v === null || v === undefined || v === '') return '';
  const n = typeof v === 'number' ? v : Number(v);
  if (Number.isNaN(n)) return String(v);
  return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 4 }).format(n);
}

function formatarDataBr(data?: string | null): string {
  if (!data) return '-';
  const partes = String(data).split('-');
  if (partes.length !== 3) return String(data);
  return `${partes[2]}/${partes[1]}/${partes[0]}`;
}

function rotuloStatusLeitura(status?: string | null): string {
  const mapa: Record<string, string> = {
    valida: 'Válida',
    corrigida: 'Corrigida',
    cancelada: 'Cancelada',
  };
  return mapa[String(status || '')] || String(status || '-');
}

function metaTipoConsumo(tipo: Medidor['tipo_consumo'] | string, unidade?: string | null): { titulo: string; icone: string; cor: string } {
  if (tipo === 'agua') return { titulo: 'Água', icone: '\u{1F4A7}', cor: '#1999d6' };
  if (tipo === 'gas') return { titulo: unidade ? `Gás - ${unidade}` : 'Gás', icone: '\u{1F525}', cor: '#ef7d21' };
  return { titulo: 'Energia elétrica', icone: '\u26A1', cor: '#f0b400' };
}

function corCampo(campo: Campo, indice: number): string {
  if (campo.principal) return '#df4f5f';
  const paleta = ['#2a7bd9', '#2a7bd9', '#111827', '#111827', '#111827', '#df4f5f'];
  return paleta[indice] || '#111827';
}

function camposPadrao(tipo: string, unidade: string): CampoForm[] {
  return [{ codigo: 'valor', rotulo: 'Valor', unidade_medida: unidade, obrigatorio: true, principal: true }];
}

function unidadePadraoConsumo(tipo: string): string {
  return tipo === 'energia_eletrica' ? 'kWh' : 'm3';
}

function criarFormMedidorPadrao(tipo = 'agua', unidade = unidadePadraoConsumo(tipo)): FormMedidorState {
  return {
    tipo_consumo: tipo,
    nome: '',
    codigo: '',
    unidade_medida: unidade,
    exigir_imagem_leitura: true,
    intervalo_leitura_dias: '',
    limite_alerta_percentual: '',
    campos: camposPadrao(tipo, unidade),
  };
}

function mapearMedidorParaForm(medidor: Medidor): FormMedidorState {
  const unidade = unidadePadraoConsumo(medidor.tipo_consumo);
  return {
    tipo_consumo: medidor.tipo_consumo,
    nome: medidor.nome || '',
    codigo: medidor.codigo || '',
    unidade_medida: unidade,
    exigir_imagem_leitura: medidor.exigir_imagem_leitura !== false,
    intervalo_leitura_dias: medidor.intervalo_leitura_dias !== null && medidor.intervalo_leitura_dias !== undefined ? String(medidor.intervalo_leitura_dias) : '',
    limite_alerta_percentual: medidor.limite_alerta_percentual !== null && medidor.limite_alerta_percentual !== undefined ? String(medidor.limite_alerta_percentual) : '',
    campos: (medidor.campos || []).map((campo) => ({
      id: campo.id,
      codigo: campo.codigo,
      rotulo: campo.rotulo,
      unidade_medida: unidade,
      obrigatorio: campo.obrigatorio,
      principal: campo.principal,
    })),
  };
}

export default function Consumo({ modo = 'medidores' }: { modo?: 'registro' | 'medidores' }) {
  const { ativoId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isConsumoDisponivel } = useAuth();
  const [ativos, setAtivos] = useState<Ativo[]>([]);
  const [medidores, setMedidores] = useState<Medidor[]>([]);
  const [leituras, setLeituras] = useState<Leitura[]>([]);
  const [reinicializacoes, setReinicializacoes] = useState<Reinicializacao[]>([]);
  const [ativoSelecionado, setAtivoSelecionado] = useState('');
  const [buscaEquipamento, setBuscaEquipamento] = useState('');
  const [medidorSelecionado, setMedidorSelecionado] = useState<number | null>(null);
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [abrirMedidor, setAbrirMedidor] = useState(false);
  const [abrirReset, setAbrirReset] = useState(false);
  const [edicaoLeitura, setEdicaoLeitura] = useState<Leitura | null>(null);
  const [fotosLeitura, setFotosLeitura] = useState<File[]>([]);
  const [fotosEdicao, setFotosEdicao] = useState<File[]>([]);
  const [medidorEmEdicaoId, setMedidorEmEdicaoId] = useState<number | null>(null);

  const podeConfigurar = ['admin_geral', 'admin_empresa', 'supervisor'].includes(user?.perfil || '');
  const podeLer = ['admin_geral', 'admin_empresa', 'supervisor', 'tecnico'].includes(user?.perfil || '');
  const modoRegistro = modo === 'registro';
  const rotaRegistroAtual = location.pathname.startsWith('/app/consumo/') ? '/app/consumo' : '/app/registro-consumo';

  const [formMedidor, setFormMedidor] = useState<FormMedidorState>(() => criarFormMedidorPadrao());
  const [formLeitura, setFormLeitura] = useState({ data_leitura: hoje(), observacao: '', valores: {} as Record<string, string> });
  const [formReset, setFormReset] = useState({ data_Reinicializacao: hoje(), motivo: '', observacao: '', novas_leituras: {} as Record<string, string> });

  const ativoAtual = useMemo(
    () => ativos.find((a) => String(a.id) === ativoSelecionado) || null,
    [ativos, ativoSelecionado],
  );
  const medidorAtual = useMemo(
    () => medidores.find((m) => m.id === medidorSelecionado) || null,
    [medidores, medidorSelecionado],
  );
  const camposAtivos = useMemo(
    () => medidorAtual?.campos.filter((campo) => campo.status === 'ativo') || [],
    [medidorAtual],
  );
  const camposAtivosPorCodigo = useMemo(
    () => Object.fromEntries(camposAtivos.map((campo) => [campo.codigo, campo])),
    [camposAtivos],
  );
  const ultimaLeituraAtual = leituras[0] || null;
  const ultimaFotoLeitura = useMemo(
    () => ultimaLeituraAtual?.midias.find((midia) => midia.tipo === 'foto') || null,
    [ultimaLeituraAtual],
  );
  const ativosFiltrados = useMemo(() => {
    const termo = buscaEquipamento.trim().toLowerCase();
    if (!termo) return ativos;
    return ativos.filter((ativo) => (
      `${ativo.nome} ${ativo.patrimonio || ''} ${ativo.marca || ''}`.toLowerCase().includes(termo)
    ));
  }, [ativos, buscaEquipamento]);

  async function carregarMedidores(ativo: string) {
    if (!ativo) {
      setMedidores([]);
      setMedidorSelecionado(null);
      return;
    }
    const data = await api<Medidor[]>(`/consumo/medidores.php?ativo_id=${ativo}`);
    setMedidores(data);
    setMedidorSelecionado((atual) => (atual && data.some((m) => m.id === atual) ? atual : (data[0]?.id || null)));
  }

  async function carregarHistorico(medidorIdAtual: number | null) {
    if (!medidorIdAtual) {
      setLeituras([]);
      setReinicializacoes([]);
      return;
    }
    const [hist, resets] = await Promise.all([
      api<Leitura[]>(`/consumo/leituras.php?medidor_id=${medidorIdAtual}`),
      api<Reinicializacao[]>(`/consumo/reinicializacoes.php?medidor_id=${medidorIdAtual}`),
    ]);
    setLeituras(hist);
    setReinicializacoes(resets);
  }

  useEffect(() => {
    if (!isConsumoDisponivel) return;
    const carregamento = modoRegistro
      ? carregarAtivosComConsumo()
      : api<Ativo[]>('/ativos/ativos.php');

    carregamento.then((listaAtivos) => {
      setAtivos(listaAtivos);
      const inicial = ativoId && listaAtivos.some((a) => String(a.id) === ativoId)
        ? ativoId
        : (listaAtivos[0] ? String(listaAtivos[0].id) : '');
      setAtivoSelecionado(inicial);
    }).catch((e) => setErro(e instanceof Error ? e.message : String(e)));
  }, [ativoId, isConsumoDisponivel, modoRegistro]);

  useEffect(() => {
    if (!isConsumoDisponivel) return;
    carregarMedidores(ativoSelecionado).catch((e) => setErro(e instanceof Error ? e.message : String(e)));
  }, [ativoSelecionado, isConsumoDisponivel]);

  useEffect(() => {
    carregarHistorico(medidorSelecionado).catch((e) => setErro(e instanceof Error ? e.message : String(e)));
  }, [medidorSelecionado]);

  useEffect(() => {
    if (!medidorAtual) return;
    const valores: Record<string, string> = {};
    medidorAtual.campos.forEach((campo) => { valores[campo.codigo] = ''; });
    setFormLeitura({ data_leitura: hoje(), observacao: '', valores });
    setFormReset({ data_Reinicializacao: hoje(), motivo: '', observacao: '', novas_leituras: valores });
  }, [medidorAtual]);

  function atualizarTipoMedidor(tipo: string) {
    setFormMedidor(criarFormMedidorPadrao(tipo));
  }

  function resetarFormMedidor() {
    setMedidorEmEdicaoId(null);
    setAbrirMedidor(false);
    setFormMedidor(criarFormMedidorPadrao());
  }

  function iniciarEdicaoMedidor(medidor: Medidor) {
    setMedidorEmEdicaoId(medidor.id);
    setAbrirMedidor(true);
    setFormMedidor(mapearMedidorParaForm(medidor));
  }

  async function enviarmidias(leituraId: number, fotos: File[]) {
    for (const foto of fotos.slice(0, 1)) {
      const fd = new FormData();
      fd.append('leitura_id', String(leituraId));
      fd.append('arquivo', foto);
      await api('/consumo/midias.php', { method: 'POST', body: fd, isForm: true });
    }
  }

  async function salvarMedidor() {
    if (!ativoSelecionado) {
      setErro('Selecione um equipamento antes de criar o medidor.');
      return;
    }
    setSalvando(true);
    setErro('');
    setMsg('');
    try {
      const body = {
        ativo_id: Number(ativoSelecionado),
        tipo_consumo: formMedidor.tipo_consumo,
        nome: formMedidor.nome.trim(),
        codigo: formMedidor.codigo.trim() || null,
        unidade_medida: unidadePadraoConsumo(formMedidor.tipo_consumo),
        exigir_imagem_leitura: formMedidor.exigir_imagem_leitura,
        intervalo_leitura_dias: formMedidor.intervalo_leitura_dias ? Number(formMedidor.intervalo_leitura_dias) : null,
        limite_alerta_percentual: formMedidor.limite_alerta_percentual || null,
        campos: formMedidor.campos.map((campo) => ({
          ...campo,
          unidade_medida: unidadePadraoConsumo(formMedidor.tipo_consumo),
        })),
      };
      if (medidorEmEdicaoId) {
        await api('/consumo/medidores.php', { method: 'PUT', body: { ...body, id: medidorEmEdicaoId } });
        setMsg('Medidor atualizado com sucesso.');
      } else {
        await api('/consumo/medidores.php', { method: 'POST', body });
        setMsg('Medidor criado com sucesso.');
      }
      resetarFormMedidor();
      await carregarMedidores(ativoSelecionado);
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setSalvando(false);
    }
  }

  async function registrarLeitura() {
    if (!medidorAtual) return;
    if (!podeLer) {
      setErro('Seu perfil não pode registrar leituras.');
      return;
    }
    if (medidorAtual.exigir_imagem_leitura !== false && !fotosLeitura.length) {
      setErro('O técnico precisa anexar ao menos uma foto da leitura.');
      return;
    }
    setSalvando(true);
    setErro('');
    setMsg('');
    try {
      const resp = await api<{ id: number }>('/consumo/leituras.php', {
        method: 'POST',
        body: {
          medidor_id: medidorAtual.id,
          data_leitura: formLeitura.data_leitura,
          observacao: formLeitura.observacao || null,
          valores: formLeitura.valores,
        },
      });
      if (fotosLeitura.length) {
        try {
          await enviarmidias(resp.id, fotosLeitura);
          setMsg('Leitura registrada com foto do consumo.');
        } catch (uploadErr) {
          setMsg('Leitura registrada, mas houve falha ao enviar alguma mídia.');
          setErro(uploadErr instanceof Error ? uploadErr.message : String(uploadErr));
        }
      } else {
        setMsg('Leitura registrada com sucesso.');
      }
      setFotosLeitura([]);
      await carregarHistorico(medidorAtual.id);
      await carregarMedidores(ativoSelecionado);
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setSalvando(false);
    }
  }

  async function salvarEdicaoLeitura() {
    if (!edicaoLeitura) return;
    setSalvando(true);
    setErro('');
    try {
      await api('/consumo/leituras.php', {
        method: 'PUT',
        body: {
          id: edicaoLeitura.id,
          data_leitura: edicaoLeitura.data_leitura,
          observacao: edicaoLeitura.observacao || null,
          valores: edicaoLeitura.valores,
        },
      });
      if (fotosEdicao.length) {
        await enviarmidias(edicaoLeitura.id, fotosEdicao);
      }
      setMsg('Última leitura atualizada.');
      setEdicaoLeitura(null);
      setFotosEdicao([]);
      await carregarHistorico(medidorSelecionado);
      await carregarMedidores(ativoSelecionado);
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setSalvando(false);
    }
  }

  async function registrarReset() {
    if (!medidorAtual) return;
    setSalvando(true);
    setErro('');
    try {
      await api('/consumo/reinicializacoes.php', {
        method: 'POST',
        body: {
          medidor_id: medidorAtual.id,
          data_Reinicializacao: formReset.data_Reinicializacao,
          motivo: formReset.motivo,
          Responsavel_id: user?.id,
          observacao: formReset.observacao,
          novas_leituras: formReset.novas_leituras,
        },
      });
      setMsg('Reinicialização registrada com sucesso.');
      setAbrirReset(false);
      await carregarHistorico(medidorAtual.id);
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setSalvando(false);
    }
  }

  function abrirEdicao(leitura: Leitura) {
    const valoresEditaveis = Object.fromEntries(
      Object.entries(leitura.valores || {}).map(([codigo, valor]) => [codigo, valorLeituraParaInput(valor)]),
    );
    setEdicaoLeitura({
      ...leitura,
      valores: valoresEditaveis,
    });
  }

  function selecionarAtivo(valor: string) {
    setAtivoSelecionado(valor);
    if (!modoRegistro) return;
    navigate(valor ? `${rotaRegistroAtual}/${valor}` : '/app/registro-consumo');
  }

  if (!isConsumoDisponivel) {
    return (
      <div className="card">
        <h2>Controle de consumo</h2>
        <p style={{ color: 'var(--texto-suave)' }}>
          O módulo de Controle de consumo precisa estar habilitado na tela de Empresas e está disponível apenas para clientes Premium no modo Padrão.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="titulo-pg">
        <h1>{modoRegistro ? 'Registro de consumo' : 'Medidores'}</h1>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn sec sm" onClick={() => {
            carregarMedidores(ativoSelecionado).catch(() => {});
            carregarHistorico(medidorSelecionado).catch(() => {});
          }}>Atualizar</button>
          {modoRegistro ? (
            <Link className="btn sec sm" to="/app/registro-consumo">Voltar para lista</Link>
          ) : (
            <Link className="btn sec sm" to="/app/registro-consumo">Abrir registro</Link>
          )}
        </div>
      </div>

      {erro && <div className="msg-erro">{erro}</div>}
      {msg && <div className="msg-ok">{msg}</div>}

      {(!modoRegistro || !ativoAtual) && (
        <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>{modoRegistro ? 'Registro de consumo' : 'Medidores'}</h3>
          <small style={{ color: 'var(--texto-suave)' }}>
            {modoRegistro
              ? 'Passo 1: selecione o equipamento. Passo 2: escolha o medidor. Passo 3: registre ou edite a leitura.'
              : 'Selecione o equipamento para cadastrar e organizar os medidores.'}
          </small>
        </div>
        <div className="grid cols2">
          <div>
            <label>Buscar equipamento</label>
            <input
              className="input"
              value={buscaEquipamento}
              onChange={(e) => setBuscaEquipamento(e.target.value)}
              placeholder="Digite nome, patrimônio ou marca"
            />
          </div>
          <div>
            <label>Equipamento</label>
            <select value={ativoSelecionado} onChange={(e) => selecionarAtivo(e.target.value)}>
              <option value="">Selecione um equipamento</option>
              {ativosFiltrados.map((ativo) => (
                <option key={ativo.id} value={ativo.id}>{ativo.nome}</option>
              ))}
            </select>
          </div>
          {medidores.length > 1 && (
            <div style={{ gridColumn: '1 / -1' }}>
              <label>Medidor</label>
              <select value={medidorSelecionado || ''} onChange={(e) => setMedidorSelecionado(e.target.value ? Number(e.target.value) : null)}>
                {medidores.map((medidor) => (
                  <option key={medidor.id} value={medidor.id}>
                    {medidor.nome} - {metaTipoConsumo(medidor.tipo_consumo, medidor.unidade_medida).titulo}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        {ativoAtual ? (
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginTop: 14 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              {ativoAtual.foto_url ? <img src={urlImagem(ativoAtual.foto_url)} alt="" className="thumb" /> : null}
              <div>
                <div><strong>{ativoAtual.nome}</strong></div>
                <div style={{ color: 'var(--texto-suave)', fontSize: 13 }}>
                  {ativoAtual.patrimonio || ativoAtual.marca || 'Sem identificação adicional'}
                </div>
                <div style={{ color: 'var(--texto-suave)', fontSize: 13, marginTop: 4 }}>
                  {medidores.length} medidor(es) cadastrado(s)
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {!modoRegistro && podeConfigurar && (
                <button
                  className="btn sec sm"
                  onClick={() => {
                    if (abrirMedidor) {
                      resetarFormMedidor();
                      return;
                    }
                    setMedidorEmEdicaoId(null);
                    setAbrirMedidor(true);
                    setFormMedidor(criarFormMedidorPadrao());
                  }}
                >
                  {abrirMedidor ? 'Fechar cadastro' : 'Novo medidor'}
                </button>
              )}
              {modoRegistro && (
                <Link className="btn sec sm" to="/app/registro-consumo">Trocar equipamento</Link>
              )}
              <Link className="btn sec sm" to="/app/ativos">Voltar aos equipamentos</Link>
            </div>
          </div>
        ) : (
          <p style={{ color: 'var(--texto-suave)', margin: '12px 0 0' }}>
            Selecione um equipamento para visualizar os medidores, registrar leituras e acompanhar o histórico.
          </p>
        )}
        </div>
      )}

      {!modoRegistro && abrirMedidor && podeConfigurar && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h3 style={{ marginTop: 0 }}>{medidorEmEdicaoId ? 'Editar medidor' : 'Novo medidor'}</h3>
          <div className="grid cols2">
            <div>
              <label>Tipo de consumo</label>
              <select value={formMedidor.tipo_consumo} onChange={(e) => atualizarTipoMedidor(e.target.value)}>
                <option value="agua">Água</option>
                <option value="gas">Gás</option>
                <option value="energia_eletrica">Energia elétrica</option>
              </select>
            </div>
            <div>
              <label>Unidade de medida</label>
              <input className="input" value={formMedidor.unidade_medida} readOnly />
            </div>
            <div>
              <label>Nome do medidor</label>
              <input className="input" value={formMedidor.nome} onChange={(e) => setFormMedidor((v) => ({ ...v, nome: e.target.value }))} />
            </div>
            <div>
              <label>Código</label>
              <input className="input" value={formMedidor.codigo} onChange={(e) => setFormMedidor((v) => ({ ...v, codigo: e.target.value }))} />
            </div>
            <div>
              <label>Intervalo de leitura (dias)</label>
              <input className="input" value={formMedidor.intervalo_leitura_dias} onChange={(e) => setFormMedidor((v) => ({ ...v, intervalo_leitura_dias: e.target.value }))} />
            </div>
            <div>
              <label>Limite de alerta (%)</label>
              <input className="input" value={formMedidor.limite_alerta_percentual} onChange={(e) => setFormMedidor((v) => ({ ...v, limite_alerta_percentual: e.target.value }))} />
            </div>
          </div>

          <label className="remember-row" style={{ marginTop: 16 }}>
            <input
              type="checkbox"
              checked={formMedidor.exigir_imagem_leitura}
              onChange={(e) => setFormMedidor((v) => ({ ...v, exigir_imagem_leitura: e.target.checked }))}
            />
            <span>
              <strong>Exigir imagem da leitura</strong>
              <small>Quando ligado, o técnico só consegue registrar o consumo com foto anexada.</small>
            </span>
          </label>

          <div className="form-actions">
            {medidorEmEdicaoId && (
              <button type="button" className="btn sec" disabled={salvando} onClick={resetarFormMedidor}>
                Cancelar
              </button>
            )}
            <button type="button" className="btn" disabled={salvando} onClick={salvarMedidor}>
              {salvando ? 'Salvando...' : (medidorEmEdicaoId ? 'Salvar alterações' : 'Salvar medidor')}
            </button>
          </div>
        </div>
      )}

      {!modoRegistro && ativoAtual && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0 }}>Medidores do equipamento</h3>
            <small style={{ color: 'var(--texto-suave)' }}>Use esta área para conferir o que já está cadastrado.</small>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 14 }}>
            {medidores.map((medidor) => (
              <button
                key={medidor.id}
                type="button"
                className="btn sec sm"
                onClick={() => setMedidorSelecionado(medidor.id)}
                style={{
                  borderColor: medidor.id === medidorSelecionado ? '#7f7ec7' : undefined,
                  boxShadow: medidor.id === medidorSelecionado ? '0 0 0 2px rgba(127,126,199,.15)' : 'none',
                }}
              >
                {medidor.nome}
              </button>
            ))}
          </div>
          {medidorAtual && podeConfigurar && (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginTop: 14 }}>
              <small style={{ color: 'var(--texto-suave)' }}>
                Imagem da leitura: {medidorAtual.exigir_imagem_leitura !== false ? 'obrigatória' : 'opcional'}
              </small>
              <button type="button" className="btn sec sm" onClick={() => iniciarEdicaoMedidor(medidorAtual)}>
                Editar medidor selecionado
              </button>
            </div>
          )}
          {!medidores.length && <div className="vazio" style={{ marginTop: 14 }}>Nenhum medidor cadastrado para este equipamento.</div>}
        </div>
      )}

      {modoRegistro && medidorAtual ? (
        <>
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ color: '#2a7bd9', fontWeight: 800, fontSize: 16, marginBottom: 8 }}>Contador</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 22, fontWeight: 800 }}>{medidorAtual.nome}</div>
                {medidorAtual.codigo && (
                  <div style={{ color: 'var(--texto-suave)', marginTop: 4 }}>Código: {medidorAtual.codigo}</div>
                )}
              </div>
              {medidores.length > 1 && (
                <div style={{ minWidth: 260 }}>
                  <label>Medidor</label>
                  <select value={medidorSelecionado || ''} onChange={(e) => setMedidorSelecionado(e.target.value ? Number(e.target.value) : null)}>
                    {medidores.map((medidor) => (
                      <option key={medidor.id} value={medidor.id}>
                        {medidor.nome} - {metaTipoConsumo(medidor.tipo_consumo, medidor.unidade_medida).titulo}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {podeConfigurar && (
                  <button className="btn sec sm" onClick={() => setAbrirReset((v) => !v)}>
                    {abrirReset ? 'Fechar reinicialização' : 'Reiniciar medidor'}
                  </button>
                )}
                <div style={{ fontSize: 18, fontWeight: 700, color: metaTipoConsumo(medidorAtual.tipo_consumo, medidorAtual.unidade_medida).cor }}>
                  {metaTipoConsumo(medidorAtual.tipo_consumo, medidorAtual.unidade_medida).titulo} {metaTipoConsumo(medidorAtual.tipo_consumo, medidorAtual.unidade_medida).icone}
                </div>
              </div>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 320px' }}>
                <div style={{ color: '#2a7bd9', fontWeight: 800, fontSize: 16, marginBottom: 10 }}>Última leitura</div>
                <div style={{ display: 'grid', gap: 6 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: 10 }}>
                    <span style={{ color: '#67a746', fontWeight: 700 }}>Data</span>
                    <span>{formatarDataBr(ultimaLeituraAtual?.data_leitura)}</span>
                  </div>
                  {camposAtivos.map((campo, indice) => (
                    <div key={`ultima-${campo.id}`} style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: 10 }}>
                      <span style={{ color: corCampo(campo, indice), fontWeight: 700 }}>{campo.rotulo}</span>
                      <span>{ultimaLeituraAtual?.valores?.[campo.codigo] ? numero(ultimaLeituraAtual.valores[campo.codigo]) : '-'}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <button className="btn sec" onClick={() => ultimaLeituraAtual && abrirEdicao(ultimaLeituraAtual)} disabled={!ultimaLeituraAtual || !podeConfigurar}>
                  Editar
                </button>
                <div style={{ width: 96, height: 96, borderRadius: 12, overflow: 'hidden', background: '#f5f7fb', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--cinza-borda)' }}>
                  {ultimaFotoLeitura ? (
                    <img src={urlImagem(ultimaFotoLeitura.arquivo_url)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <span style={{ color: 'var(--texto-suave)', fontSize: 12, textAlign: 'center', padding: 8 }}>Sem imagem</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ color: '#204b8f', fontWeight: 800, fontSize: 18, marginBottom: 14 }}>Registrar nova leitura</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap', paddingBottom: 12, marginBottom: 14, borderBottom: '1px solid var(--cinza-borda)' }}>
              <div style={{ color: '#df4f5f', fontWeight: 800, fontSize: 16 }}>Data de registro</div>
              <input className="input" type="date" value={formLeitura.data_leitura} onChange={(e) => setFormLeitura((v) => ({ ...v, data_leitura: e.target.value }))} style={{ maxWidth: 220 }} />
            </div>

            <div style={{ display: 'grid', gap: 18 }}>
              {camposAtivos.map((campo, indice) => (
                <div key={campo.id} style={{ display: 'grid', gridTemplateColumns: 'minmax(130px, 190px) 1fr', gap: 14, alignItems: 'center' }}>
                  <label style={{ color: corCampo(campo, indice), fontWeight: 500, fontSize: 16 }}>
                    {campo.rotulo}{campo.obrigatorio ? ':' : ''}
                  </label>
                  <input
                    className="input"
                    value={formLeitura.valores[campo.codigo] || ''}
                    onChange={(e) => setFormLeitura((v) => ({ ...v, valores: { ...v.valores, [campo.codigo]: e.target.value } }))}
                    placeholder={campo.unidade_medida || medidorAtual.unidade_medida}
                    style={{ fontSize: 18, textAlign: 'right' }}
                  />
                </div>
              ))}
            </div>

            <div style={{ marginTop: 24 }}>
              <div style={{ border: '1px solid #e8e8ee', borderRadius: 18, padding: 18, background: '#fff' }}>
                <label style={{ display: 'block', fontWeight: 700, marginBottom: 10 }}>
                  Imagem da leitura {medidorAtual.exigir_imagem_leitura !== false ? '*' : '(opcional)'}
                </label>
                <SeletorFotos arquivos={fotosLeitura} onChange={setFotosLeitura} max={1} />
                <small style={{ color: 'var(--texto-suave)', display: 'block', marginTop: 6 }}>
                  {medidorAtual.exigir_imagem_leitura !== false
                    ? 'Clique para adicionar a foto do medidor. Apenas uma imagem é obrigatória por leitura.'
                    : 'Clique para adicionar a foto do medidor. A imagem é opcional para este medidor.'}
                </small>
              </div>
            </div>

            <div style={{ marginTop: 14 }}>
              <label>Observação</label>
              <textarea className="input" value={formLeitura.observacao} onChange={(e) => setFormLeitura((v) => ({ ...v, observacao: e.target.value }))} rows={3} />
            </div>

            <div className="form-actions" style={{ marginTop: 20 }}>
              <button type="button" className="btn" disabled={salvando} onClick={registrarLeitura} style={{ minWidth: 220 }}>
                {salvando ? 'Salvando...' : 'Registrar'}
              </button>
            </div>
          </div>

          {abrirReset && podeConfigurar && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <h3 style={{ margin: 0 }}>Reinicialização do medidor</h3>
                <button className="btn sec sm" onClick={() => setAbrirReset(false)}>Fechar</button>
              </div>
              <div className="grid cols2" style={{ marginTop: 14 }}>
                <div>
                  <label>Data</label>
                  <input className="input" type="date" value={formReset.data_Reinicializacao} onChange={(e) => setFormReset((v) => ({ ...v, data_Reinicializacao: e.target.value }))} />
                </div>
                <div>
                  <label>Motivo</label>
                  <input className="input" value={formReset.motivo} onChange={(e) => setFormReset((v) => ({ ...v, motivo: e.target.value }))} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label>Observação</label>
                  <textarea className="input" value={formReset.observacao} onChange={(e) => setFormReset((v) => ({ ...v, observacao: e.target.value }))} />
                </div>
                {camposAtivos.map((campo) => (
                  <div key={`reset-${campo.id}`}>
                    <label>Nova leitura inicial: {campo.rotulo}</label>
                    <input
                      className="input"
                      value={formReset.novas_leituras[campo.codigo] || ''}
                      onChange={(e) => setFormReset((v) => ({ ...v, novas_leituras: { ...v.novas_leituras, [campo.codigo]: e.target.value } }))}
                    />
                  </div>
                ))}
              </div>
              <div className="form-actions">
                <button type="button" className="btn" disabled={salvando} onClick={registrarReset}>
                  {salvando ? 'Salvando...' : 'Registrar reinicialização'}
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        modoRegistro && ativoAtual && <div className="card"><div className="vazio">Selecione um medidor para continuar.</div></div>
      )}

      {modoRegistro && (
      <div className="grid cols2" style={{ alignItems: 'start', marginTop: 16 }}>
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Histórico de leituras</h3>
          <div style={{ display: 'grid', gap: 12 }}>
            {leituras.map((leitura, idx) => (
              <div key={leitura.id} className="card" style={{ margin: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'start' }}>
                  <div>
                    <strong>{formatarDataBr(leitura.data_leitura)}</strong>
                    <div style={{ color: 'var(--texto-suave)', fontSize: 13 }}>Registrada por {leitura.usuario_nome || 'Usuário'}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <span className="badge">{rotuloStatusLeitura(leitura.status)}</span>
                    {idx === 0 && podeConfigurar && <button className="btn sec sm" onClick={() => abrirEdicao(leitura)}>Editar última</button>}
                  </div>
                </div>
                <div className="grid cols2" style={{ marginTop: 10 }}>
                  {Object.entries(leitura.valores).map(([codigo, valor]) => (
                    <div key={codigo}>
                      <div style={{ fontSize: 12, color: 'var(--texto-suave)' }}>{camposAtivosPorCodigo[codigo]?.rotulo || codigo}</div>
                      <strong>{numero(valor)}</strong>
                      <div style={{ fontSize: 12, color: '#0A49C2' }}>
                        Consumo: {leitura.consumos[codigo] !== null ? numero(leitura.consumos[codigo] || 0) : '-'}
                      </div>
                    </div>
                  ))}
                </div>
                {leitura.observacao && <p style={{ marginBottom: 0 }}>{leitura.observacao}</p>}
                {!!leitura.midias.filter((midia) => midia.tipo === 'foto').length && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                    {leitura.midias.filter((midia) => midia.tipo === 'foto').map((midia) => (
                      <a key={midia.id} href={urlImagem(midia.arquivo_url)} target="_blank" rel="noreferrer" className="btn sec sm">
                        Imagem
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {!leituras.length && <div className="vazio">Nenhuma leitura registrada ainda.</div>}
          </div>
        </div>

        <div className="card">
          <h3 style={{ marginTop: 0 }}>Histórico de reinicializações</h3>
          <div style={{ display: 'grid', gap: 12 }}>
            {reinicializacoes.map((reset) => (
              <div key={reset.id} className="card" style={{ margin: 0 }}>
                <strong>{formatarDataBr(reset.data_Reinicializacao)}</strong>
                <div style={{ color: 'var(--texto-suave)', fontSize: 13 }}>{reset.motivo} • {reset.responsavel_nome || 'Responsável'}</div>
                <p>{reset.observacao}</p>
                <div className="grid cols2">
                  <div>
                    <div style={{ fontWeight: 700 }}>Leituras anteriores</div>
                    {Object.entries(reset.leituras_anteriores || {}).map(([codigo, valor]) => (
                      <div key={codigo}>{codigo}: {numero(valor)}</div>
                    ))}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700 }}>Novas leituras</div>
                    {Object.entries(reset.novas_leituras || {}).map(([codigo, valor]) => (
                      <div key={codigo}>{codigo}: {numero(valor)}</div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
            {!reinicializacoes.length && <div className="vazio">Nenhuma reinicialização registrada.</div>}
          </div>
        </div>
      </div>
      )}

      {edicaoLeitura && (
        <div className="modal-bg" onClick={() => setEdicaoLeitura(null)} style={{ background: 'transparent', backdropFilter: 'none' }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 760, paddingTop: 0 }}>
            <div style={{ margin: '0 -20px 16px', padding: '16px 20px', background: '#f8f8fb', borderBottom: '1px solid #ececf3', borderTopLeftRadius: 18, borderTopRightRadius: 18, textAlign: 'center', color: '#2a8bdb', fontSize: 20, fontWeight: 800 }}>
              Edição da última leitura
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap', paddingBottom: 12, marginBottom: 14, borderBottom: '1px solid var(--cinza-borda)' }}>
              <div style={{ color: '#df4f5f', fontWeight: 800, fontSize: 16 }}>Data de registro</div>
              <input className="input" type="date" value={edicaoLeitura.data_leitura} onChange={(e) => setEdicaoLeitura((atual) => (atual ? { ...atual, data_leitura: e.target.value } : atual))} style={{ maxWidth: 220 }} />
            </div>
            <div style={{ display: 'grid', gap: 18 }}>
              {camposAtivos.map((campo, indice) => (
                <div key={`editar-${campo.id}`} style={{ display: 'grid', gridTemplateColumns: 'minmax(130px, 190px) 1fr', gap: 14, alignItems: 'center' }}>
                  <label style={{ color: corCampo(campo, indice), fontWeight: 500, fontSize: 16 }}>{campo.rotulo}</label>
                  <input
                    className="input"
                    value={edicaoLeitura.valores[campo.codigo] || ''}
                    onChange={(e) => setEdicaoLeitura((atual) => (atual ? { ...atual, valores: { ...atual.valores, [campo.codigo]: e.target.value } } : atual))}
                    style={{ fontSize: 18, textAlign: 'right' }}
                  />
                </div>
              ))}
            </div>
            <div style={{ marginTop: 16 }}>
              <label>Observação</label>
              <input className="input" value={edicaoLeitura.observacao || ''} onChange={(e) => setEdicaoLeitura((atual) => (atual ? { ...atual, observacao: e.target.value } : atual))} />
            </div>
            <div style={{ marginTop: 12 }}>
              <label>Imagem da leitura</label>
              <SeletorFotos arquivos={fotosEdicao} onChange={setFotosEdicao} max={1} />
            </div>
            <div className="form-actions">
              <button type="button" className="btn sec" onClick={() => setEdicaoLeitura(null)}>Cancelar</button>
              <button type="button" className="btn" disabled={salvando} onClick={salvarEdicaoLeitura} style={{ minWidth: 180 }}>
                {salvando ? 'Salvando...' : 'Atualizar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


